import { createWebHashHistory, createRouter } from 'vue-router';
import { FAQ_BASE_PATH } from '@/constants/faqRouterPathes';
import TheFallback from '@/pages/TheFallback';
import FaqItem from '@/pages/FaqItem';
import TheFeedback from '@/pages/TheFeedback';
import MeyaChat from '@/pages/MeyaChat';
import PageNotFound from '@/pages/PageNotFound';
import TheDocument from '@/pages/TheDocument';
import {
    fetchAppConfigIfEmpty,
    fetchDocumentsIfEmpty,
    fetchFaqDataIfEmpty,
    fetchFeedbackQuestionsIfEmpty,
    removePreloader,
    getTargetFaqPage,
} from '@/router/helpers';
import store from '@/store';
import checkValidParams from '@/middleware/paramsValidate';
import middlewarePipeline from '@/middleware/middlewarePipeline';
import { POST_MESSAGES } from '@/constants/postMessages';
import { isMeyaHWMGame, isMeyaSplit, isFirstEnter, removeFirstEnter } from '@/helpers/meyaHandler';
import supportMeyaVariant from '@/helpers/meyaHandler';

const routes = [
    {
        name: 'Home',
        route: '/',
        beforeEnter: async(to, from, next) => {
            await fetchFaqDataIfEmpty()


            if ('preset' in store.state.queryParams) {
                next('/feedback')
                return
            }
            if (store.state.FAQ.isFaqTreeExist) {
                return next(store.getters['FAQ/getRootTreeItemPath'])
            }
            next('/feedback')
        },
    },
    {
        path: `${FAQ_BASE_PATH}/:id*`,
        component: FaqItem,
        beforeEnter: async(to, from, next) => {
            await fetchFaqDataIfEmpty()

            if (isMeyaSplit() && supportMeyaVariant() === 1 && isFirstEnter) {
                removeFirstEnter()
                next('/meya_chat')
                return
            }

            if (store.state.FAQ.isFaqTreeExist) {
                if (to.fullPath === FAQ_BASE_PATH) {
                    next(store.getters['FAQ/getRootTreeItemPath'])
                    return
                }
                removePreloader()
                next()
                return
            }
            next('/feedback')
        },
        meta: {
            headerTitle: 'faq.header',
        },
    },
    {
        name: 'Feedback',
        path: '/feedback',
        component: TheFeedback,
        beforeEnter: async(to, from, next) => {
            window.parent.postMessage(POST_MESSAGES.OPEN_FEEDBACK, '*');

            await fetchFeedbackQuestionsIfEmpty();

            if (!store.state.feedback.feedbackQuestionList.data.length) {
                next('/fallback')
                return
            }

            if ('preset' in store.state.queryParams) {
                await store.dispatch('feedback/fetchPreset')
            }

            removePreloader()
            next()
        },
        meta: {
            headerTitle: 'feedback.header',
        },
    },
    {
        name: 'MeyaChat',
        path: '/meya_chat',
        component: MeyaChat,
        beforeEnter: async(to, from, next) => {
            if (isMeyaHWMGame()) {
                removePreloader()
                next()
            } else {
                next({ name: '404' })
            }
        },
        meta: {
            headerTitle: 'chat',
        },
    },
    {
        name: 'Fallback',
        path: '/fallback',
        component: TheFallback,
        beforeEnter: [
            removePreloader,
        ],
        meta: {
            headerTitle: 'feedback.header',
        },
    },
    {
        path: '/documents/:link',
        component: TheDocument,
        beforeEnter: async(to, from, next) => {
            await fetchDocumentsIfEmpty()
            removePreloader()

            const isDocumentExist = !!store.state.documents.documentList.find(doc => doc.link === to.params.link)
            if (isDocumentExist) next()
            else next({ name: '404' })
        },
        meta: {
            headerTitle: 'doc.header',
            middleware: [
                checkValidParams
            ]
        }
    },
    {
        name: '404',
        path: '/:pathMatch(.*)*',
        component: PageNotFound,
        beforeEnter: [
            removePreloader,
        ],
        meta: {
            headerTitle: 'faq.header',
        },
    }
];

const router = createRouter({
    history: createWebHashHistory(),
    routes
})

router.beforeEach(async(to, from, next) => {
    store.commit('routing/setReferer', to.redirectedFrom?.fullPath || from.fullPath);

    await fetchAppConfigIfEmpty()

    if (store.getters['appConfig/getAppConfigEmptyStatus']) {
        if (to.fullPath === '/fallback') return next()
        return next('/fallback')
    }

    if (store.state.routing.isRedirectToFaqPage) {
        await fetchFaqDataIfEmpty()
        const targetFaqPage = getTargetFaqPage();
        if (targetFaqPage) {
            next(targetFaqPage.fullPath)
        } else {
            next({ name: '404' })
        }
        await store.commit('routing/setRedirectToFaqPage', false)
        return
    }

    if (!to.meta?.middleware) return next()

    return middlewarePipeline(to.meta.middleware, { to, from, next })
})

export default router
